// React
import React, { Component } from "react";

// Libraries
import Moment from 'moment';
import Numeral from 'numeral';
import { BarChart, Bar, ReferenceLine, ResponsiveContainer, Rectangle } from 'recharts';

// ErrorHandler
import { ErrorHandler } from '../../Utils';

// Panel
import { PanelHeading, PanelBody, Dropdown } from '../../Components';

// Styles
import "./dashboard.css";

// Stores 
import { UserProfileStore, SampleDataStore, CurrencyStore } from '../../Stores';

// Custom Bar for Chart
const CustomBar = (props) => {

    const {value} = props;

    var fill = value > 0 ? '#0aefaa' : '#8884d8';
  
    //use explicit fill here, or use the additional css class and make a css selector to update fill there
    return (<Rectangle {...props} fill={fill} stroke="none"/>);
    
  };

/**********************************************************
 * 
 *  Historical pnl shows the users daily pnl
 *  
 **********************************************************/

class DailyPnl extends Component {

/**********************************************************
 *  Constructor
 **********************************************************/
    constructor(props) {

        super(props);

        this.state = {
            data : [],
            selectedBar : {},
            quoteCurrency : 'USD',
            currencySymbol : CurrencyStore.getCurrencySymbol('USD'),
            decimals : CurrencyStore.getCurrencyFormat(),
            arrowClass : 'icon-arrow-up8 green-text',
            description: ["Daily total profit and loss numbers from the last x number of days.", "Click on the bars to see how much you profited or lost on that day."],
            loading: true,
            message: null
        };
    }


/**********************************************************
 *  Component Lifecycle Functions
 **********************************************************/

    // After first render, fetch all parameters and the corresponding chart
    componentDidMount() {

        //console.log('HistoricalPnl.componentDidMount()');

        // If user logged in, listen for updates
        // and fetch the hist pnl data
        if(this.props.user !== null) {
            this.listenForUpdates();
            this.getHistPnl();
        }
        else {
            this.getSampleHistPnl();
        }

    }

/**********************************************************
 *  Helper Functions
 **********************************************************/

    // Retrieves and displays the hist pnl
    getHistPnl() {

        //console.log('HistoricalPnl.getHistPnl()');

        const summaries = UserProfileStore.getHistExchangeSummaries();

        if(summaries !== null) {

            const cleaned = this.cleanPnl(summaries);

            this.setState((prev, props) => {
                return {
                    loading: false,
                    data : cleaned,
                    selectedBar : cleaned[this.state.quoteCurrency][cleaned[this.state.quoteCurrency].length-1]
                }
            });
 
        }

    }


    // Retrieves and displays the sample hist pnl
    getSampleHistPnl() {

        const sampleHistPnl = SampleDataStore.getDailyPnlData();
        
        this.setState((prev, props) => {
            return {
                loading : false,
                data : sampleHistPnl,
                selectedBar : sampleHistPnl[this.state.quoteCurrency][sampleHistPnl[this.state.quoteCurrency].length-1]
            }
        });
    }


    // Listen for updates to hist pnl
    listenForUpdates() {
        
        var emitter = UserProfileStore.getEmitter();

        // Listen for pnl updates
        emitter.addListener('histPnl', (updated) => {

            if(updated) {
                this.getHistPnl();
            }
            else {

                const error = UserProfileStore.getHistPnlError();

                var errorMessage = ErrorHandler.historicalPnlError(error);

                this.setState((prev, props) => {
                    return {
                        message : errorMessage,
                        loading : false
                    }
                });
               
            }

        });

        // Listen for loading indicator
        emitter.addListener('loadingHistPnl', (loading) => {

            if(loading) {
                this.setState((prev, props) => {
                    return {
                        loading : true
                    }
                });
            }

        });

    }

    
    // Formats the hist pnl
    cleanPnl(summaries) {

        var formatted = {};

        for(let[currency, dates] of Object.entries(summaries)) {

            var days = [];

            for(let[timestamp, exchanges] of Object.entries(dates)) {

                var total = 0;
                var value = 0;

                for(let[name, details] of Object.entries(exchanges)) {

                    total += details.position;
                    value += details.dailyPnl;

                }
            
                var dailyPnlObj = {
                    date : timestamp,
                    value : value !== 0 ? value : 0,
                    change : value !== 0 ? (value/total)*100 : 0,
                    total : total
                };

                days.push(dailyPnlObj);

            }

            formatted[currency] = days.sort( (a,b) => {
                if(a.date < b.date)
                    return -1;
                if(a.date > b.date)
                    return 1;
                return 0;
            });

        }

        return formatted;

    }

    // Hover over a bar in graph to see its details below
    selectBar(data, index) {

        //console.log('HistoricalPnl.selectBar()');

        var arrowClass = data.payload.change > 0 ? 'icon-arrow-up8 green-text' : 'icon-arrow-down8 red-text'

        this.setState((prev, props) => {
            return {
                selectedBar : data.payload,
                arrowClass : arrowClass
            }
        });

    }

    // Selecing a quote currency will update the dropdown,
    // decimal formatting and the currency symbol
    selectQuoteCurrency(ticker) {

        this.setState((prev, props) => {
            return {
                quoteCurrency : ticker,
                currencySymbol : CurrencyStore.getCurrencySymbol(ticker),
                decimals : CurrencyStore.getFormat(ticker)
            }
        });

    }

    // Refreshes the hist pnl
    refreshHistPnl() {

        if(this.props.user !== null) {
            UserProfileStore.getHistPnl(true, 7);
        }
        else {
            // tell user to signup
        }

    }

/**********************************************************
 *  Render
 **********************************************************/

  render() {

    //console.log('HistoricalPnl.render()');

    var data = this.state.data[this.state.quoteCurrency];

    return (

        <div className="panel panel-flat bg-theme-700">

            <PanelHeading title={"Daily PnL"} description={this.state.description} sample={this.props.user === null ? true : false} 
            hiddenMenu={true} refresh={null}>

                <Dropdown icon='icon-coins' value={this.state.quoteCurrency}>
                    <li onClick={() => this.selectQuoteCurrency('USD')}><a href={null}><div className='currency-symbol'>{CurrencyStore.getCurrencySymbol('USD')}</div>USD</a></li>
                    <li onClick={() => this.selectQuoteCurrency('BTC')}><a href={null}><div className='currency-symbol'>{CurrencyStore.getCurrencySymbol('BTC')}</div>BTC</a></li>
                </Dropdown>

                <Dropdown icon='icon-calendar' value={'Last Week'}>
                    <li><a href={null}>Last Week</a></li>
                    <li><a href={null}>Last Month</a></li>
                    <li><a href={null}>Since Signup</a></li>
                </Dropdown>

            </PanelHeading>

            <PanelBody loading={this.state.loading} message={this.state.message}>

                <ResponsiveContainer width="100%" height={175}>

                    <BarChart data={data}
                    margin={{top: -25, right: 0, left: 0, bottom: 10}}>
    
                        <ReferenceLine y={0} stroke='rgba(0,0,0,0)'/>
                        <Bar dataKey="value" shape={CustomBar} onClick={this.selectBar.bind(this)} onMouseEnter={this.selectBar.bind(this)}/>
                    
                    </BarChart>

                </ResponsiveContainer>

                <div className="flex-panel">

                    <div className="child-row">

                        <div className="rox-box left">

                            <div>
                                <span className="right-pad">{ this.state.currencySymbol }</span>
                                { Numeral(this.state.selectedBar.value).format(this.state.decimals) }
                                <i className={ this.state.selectedBar.value >= 0 ? 'icon-arrow-up8 green-text left-pad arrow' : 'icon-arrow-down8 red-text left-pad arrow'}></i>
                            </div>
                            <div className="lowlight">
                                { parseFloat(this.state.selectedBar.change).toFixed(2) }  
                                <span className="left-pad">%</span>
                            </div>

                        </div>

                        <div className="rox-box right">

                            <div>
                                { Moment(parseInt(this.state.selectedBar.date)).utc().format('MMM DD, YYYY') }
                            </div>
                            <div>
                                <span className="right-pad">{ this.state.currencySymbol }</span>
                                { Numeral(this.state.selectedBar.total).format(this.state.decimals) }
                            </div>

                        </div>

                    </div>


                </div>
            
            </PanelBody>

        </div>

    );
  }
}

export default DailyPnl;







// WEBPACK FOOTER //
// ./src/Views/Dashboard/DailyPnl.js